import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MailOpen, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ContactMessageResponse } from "../../features/contact-messages/contracts/ContactMessageResponse";
import {
  selectContactMessages,
  selectContactMessagesListStatus,
} from "../../features/contact-messages/contact-messages-slice";
import {
  deleteContactMessage,
  fetchContactMessages,
  markContactMessageRead,
} from "../../features/contact-messages/slice-extensions/contact-messages-slice-extension";

const statusStyles: Record<ContactMessageResponse["status"], string> = {
  New: "bg-primary-95 text-primary-40 dark:bg-primary-20 dark:text-primary-90",
  Read: "bg-grey-95 text-grey-40 dark:bg-primary-10 dark:text-grey-80",
};

/** The instructor's inbox of contact messages from the booking page / widget. */
const ContactMessagesList = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectContactMessages);
  const listStatus = useAppSelector(selectContactMessagesListStatus);

  useEffect(() => {
    dispatch(fetchContactMessages({}));
  }, [dispatch]);

  if (listStatus === "loading" && messages.length === 0) {
    return <p className="text-sm text-grey-40 dark:text-grey-80">{t("common.loading", "Loading…")}</p>;
  }

  if (messages.length === 0) {
    return (
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {t(
          "contactMessages.empty",
          "No messages yet. Visitors can write to you from your booking page and the contact widget.",
        )}
      </p>
    );
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(iso),
    );

  return (
    <ul className="flex flex-col gap-3">
      {messages.map((message) => (
        <li key={message.id} className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium text-grey-20 dark:text-white">{message.name}</p>
              <p className="text-sm text-grey-40 dark:text-grey-80">
                {message.email}
                {message.phone && ` · ${message.phone}`} · {formatDate(message.dateCreated)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusStyles[message.status]}`}
              >
                {t(`contactMessages.status.${message.status}`, message.status)}
              </span>
              <button
                type="button"
                aria-label={t("contactMessages.delete", "Delete message")}
                title={t("contactMessages.delete", "Delete message")}
                className="text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
                onClick={() => dispatch(deleteContactMessage(message.id))}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <p className="mt-2 text-sm text-grey-20 dark:text-white">{message.message}</p>

          {message.status === "New" && (
            <button
              type="button"
              className="btn btn-secondary mt-3"
              onClick={() => dispatch(markContactMessageRead(message.id))}
            >
              <MailOpen size={16} />
              {t("contactMessages.markRead", "Mark as read")}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
};

export default ContactMessagesList;
